import React from 'react';
import { Box, Typography, Button, Container, Grid, Chip, Stack, Skeleton } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LanguageIcon from '@mui/icons-material/Language';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import CodeIcon from '@mui/icons-material/Code';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

const HeroSection = ({data}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const isLoading = data === undefined;

    return (
        <Box
            id="home"
            sx={{
                background: theme.palette.custom.heroGradient,
                color: theme.palette.text.white,
                pt: 10,
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    {/* Trusted Badge */}
                    <Chip
                        icon={<FiberManualRecordIcon />}
                        label={t('pages.home.hero.trusted_badge')}
                        sx={{
                            backgroundColor: theme.palette.custom.heroBadgeBg,
                            color: theme.palette.common.white,
                            backdropFilter: 'blur(10px)',
                            mb: 2,
                            px: 1,
                            border: `1px solid ${theme.palette.custom.heroBorder}`,

                            '& .MuiChip-icon': {
                                color: theme.palette.secondary.light,
                                fontSize: 12,
                                animation: 'pulse 2s cubic-bezier(.4, 0, .6, 1) infinite',
                            },
                            '@keyframes pulse': {
                                '0%, 100%': {
                                    opacity: 1,
                                    transform: 'scale(1)',
                                },
                                '50%': {
                                    opacity: 0.5,
                                    transform: 'scale(1.15)',
                                },
                            },
                            '& .MuiChip-label': {
                                fontSize: '1rem',
                                fontWeight: 600,
                                letterSpacing: '0.02em',
                                px: 1.5,
                            },
                        }}
                    />

                    {/* Main Headline */}
                    <Typography
                        variant="h1"
                        component="h1"
                        sx={{
                            mb: 2,
                            fontWeight: 700,
                            fontSize: {
                                xs: '2.6rem',   // mobile
                                sm: '3.5rem',   // tablets
                                md: '4.5rem',   // desktop
                            },
                            lineHeight: 1.1,
                        }}
                    >
                        {isLoading ? (
                            <Box 
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Skeleton variant="text" height={60} width="80%" />
                            </Box>
                        ) : (
                            data?.meta?.title1 || t('pages.home.hero.headline_prefix')
                        )}<br />
                        <Box
                            component="span"
                            sx={{
                                background: theme.palette.custom.accentGradient,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                textFillColor: 'transparent',
                            }}
                        >
                            {isLoading ? (
                                <Box 
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Skeleton variant="text" height={60} width="60%" />
                                </Box>
                            ) : (
                                data?.meta?.title2 || t('pages.home.hero.headline_highlight')
                            )}
                        </Box>
                    </Typography>

                    {/* Subheadline */}
                    <Typography variant="h6" sx={{ color: theme.palette.text.heroSecondary, fontSize: '1.25rem', mb: 4, maxWidth: '650px', mx: 'auto', opacity: 0.9, fontWeight: 'normal', lineHeight: 1.75 }}>
                        {isLoading ? (
                            <Box 
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Skeleton variant="text" height={40} width="90%" />
                            </Box>
                        ) : (
                            data?.meta?.description || t('pages.home.hero.subheadline')
                        )}
                    </Typography>

                    {/* Action Buttons */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mb: 6 }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                backgroundColor: theme.palette.common.white,
                                color: theme.palette.primary.main,
                                px: 2,
                                py: 1,
                                borderRadius: '8px',
                                fontSize: '1.2rem',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                },
                            }}
                        >
                            {t('pages.home.hero.view_work')}
                        </Button>
                        <Button
                            variant="contained"
                            size="large"
                            sx={{
                                backgroundColor: theme.palette.common.white,
                                color: theme.palette.primary.main,
                                px: 2,
                                py: 1,
                                borderRadius: '8px',
                                fontSize: '1.2rem',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                },
                            }}
                        >
                            {t('pages.home.hero.start_project')}
                        </Button>
                    </Stack>

                    {/* Stats Cards */}
                    {/* CLIENT + DURATION */}
                    {isLoading ? (
                        <Grid container spacing={3} mt={5} justifyContent="center">
                            <Grid size={{ xs: 12, md: 3 }}>
                            <Skeleton variant="rounded" height={100} />
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                            <Skeleton variant="rounded" height={100} />
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                            <Skeleton variant="rounded" height={100} />
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container spacing={3} justifyContent="center">
                            <Grid item xs={12} sm={4} md={3}>
                                <Box
                                    sx={{
                                        backgroundColor: theme.palette.custom.statCardBg,
                                        px: 5,
                                        py: 1,
                                        border: `1px solid ${theme.palette.custom.glassBorder}`,
                                        backdropFilter: 'blur(10px)',
                                        display: 'flex',
                                        borderRadius: '8px',
                                        alignItems: 'center',
                                        textAlign: 'left',
                                        '&:hover': {
                                            backgroundColor: theme.palette.custom.statHover,
                                        },
                                    }}
                                >
                                    <LanguageIcon sx={{ fontSize: 30, color: theme.palette.secondary.main, mr: 2 }} />
                                    <Box>
                                        <Typography variant="body2" fontSize="1.5rem" fontWeight="bold">{data?.meta?.web_development}</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>{t('pages.home.hero.stats_web_dev')}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <Box
                                    sx={{
                                        backgroundColor: theme.palette.custom.statCardBg,
                                        px: 5,
                                        py: 1,
                                        border: `1px solid ${theme.palette.custom.glassBorder}`,
                                        backdropFilter: 'blur(10px)',
                                        display: 'flex',
                                        borderRadius: '8px',
                                        alignItems: 'center',
                                        textAlign: 'left',
                                        '&:hover': {
                                            backgroundColor: theme.palette.custom.statHover,
                                        },
                                    }}
                                >
                                    <SmartphoneIcon sx={{ fontSize: 30, color: theme.palette.secondary.main, mr: 2 }} />
                                    <Box>
                                        <Typography variant="body2" fontSize="1.5rem" fontWeight="bold">{data?.meta?.mobile_apps}</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>{t('pages.home.hero.stats_mobile_apps')}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <Box
                                    sx={{
                                        backgroundColor: theme.palette.custom.statCardBg,
                                        px: 5,
                                        py: 1,
                                        border: `1px solid ${theme.palette.custom.glassBorder}`,
                                        backdropFilter: 'blur(10px)',
                                        display: 'flex',
                                        borderRadius: '8px',
                                        alignItems: 'center',
                                        textAlign: 'left',
                                        '&:hover': {
                                            backgroundColor: theme.palette.custom.statHover,
                                        },
                                    }}
                                >
                                    <CodeIcon sx={{ fontSize: 30, color: theme.palette.secondary.main, mr: 2 }} />
                                    <Box>
                                        <Typography variant="body2" fontSize="1.5rem" fontWeight="bold">{data?.meta?.custom_solutions}</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>{t('pages.home.hero.stats_custom_solutions')}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    )

                    }

                                
                    
                </Box>
            </Container>
        </Box>
    );
};

export default HeroSection;
